/**
 * Cross-document hand-offs between the home or PDP deck and a product page.
 *
 * The card-to-PDP morph: card.js names the clicked card's colour field `card-field` at click time,
 * and the PDP names its gallery field the same (docs/contracts.md, page transitions). Just before
 * the old page is captured (`pageswap`), this keeps the name only on the clicked card, and only
 * when the navigation goes to that card's product. Anything else named `card-field` (the PDP's own
 * field when a "Next in the deck" card is clicked) gives way, because two elements with one name
 * cancel the transition. No support, or no match: a plain page load.
 *
 * Size in Space on arrival: /products/x#size-in-space (the home teaser's CTA) opens the modal
 * through its own trigger, so focus returns there on close.
 */
const NAME = 'card-field';

const pathOf = (url) => new URL(url, 'https://x.invalid').pathname.replace(/\/$/, '');

/**
 * Keeps `card-field` only on a card whose link points at `destination` and clears every other.
 * Returns the cleared elements that are not cards (a page's own field), to name again on return.
 */
export function settleMorph(named, destination) {
  let kept = false;
  const cleared = [];
  for (const element of named) {
    const href = element.closest('.card__link')?.href;
    const keep = !kept && href != null && pathOf(href) === pathOf(destination);
    kept ||= keep;
    if (keep) continue;
    element.style.viewTransitionName = '';
    if (href == null) cleared.push(element);
  }
  return cleared;
}

/** Opens the dialog a URL hash names, through the element that controls it. */
export function openFromHash(hash, doc = globalThis.document) {
  const id = hash.slice(1);
  if (!/^[\w-]+$/.test(id)) return;
  doc.querySelector(`[aria-controls="${id}"]`)?.click();
}

function namedFields() {
  return [...document.querySelectorAll('[style*="view-transition-name"]')].filter(
    (element) => element.style.viewTransitionName === NAME,
  );
}

if (globalThis.window && globalThis.document) {
  let cleared = [];
  window.addEventListener('pageswap', (event) => {
    const destination = event.activation?.entry?.url;
    if (event.viewTransition && destination) cleared = settleMorph(namedFields(), destination);
  });

  // Back to this page out of the page cache: its own field is the morph target again.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    for (const element of cleared) element.style.viewTransitionName = NAME;
    cleared = [];
  });

  if (location.hash === '#size-in-space') {
    customElements.whenDefined('size-in-space').then(() => openFromHash(location.hash));
  }
}
